import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown } from 'lucide-react';

interface ColorPickerFieldProps {
  value: string;
  onChange: (color: string) => void;
  colorOptions: { name: string; value: string }[];
}

const ColorPickerField: React.FC<ColorPickerFieldProps> = ({ value, onChange, colorOptions }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Position the portal menu below the trigger button
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 280)
    });
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Reposition on scroll/resize while open
  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  // Case-insensitive hex match (backend may store #3eb489, config has #3EB489)
  const selected = colorOptions.find(c => c.value.toLowerCase() === value?.toLowerCase());

  const handleOpen = () => {
    updatePosition();
    setIsOpen(!isOpen);
  };

  return (
    <div style={{ marginBottom: '16px' }}>
      <label style={{
        display: 'block',
        marginBottom: '6px',
        fontSize: '13px',
        fontWeight: '500',
        color: '#374151'
      }}>
        Color
      </label>

      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        onClick={handleOpen}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          width: '100%',
          padding: '8px 12px',
          backgroundColor: '#fff',
          border: `1px solid ${isOpen ? '#6366f1' : '#d1d5db'}`,
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: '13px',
          color: '#111827',
          transition: 'border-color 0.15s',
          outline: 'none'
        }}
      >
        <span style={{
          width: '22px',
          height: '22px',
          borderRadius: '6px',
          backgroundColor: value || '#d1d5db',
          border: '1px solid rgba(0,0,0,0.1)',
          flexShrink: 0
        }} />
        <span style={{ flex: 1, textAlign: 'left' }}>
          {selected?.name || (value ? value.toUpperCase() : 'Select color')}
        </span>
        <ChevronDown
          size={16}
          style={{
            color: '#6b7280',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s'
          }}
        />
      </button>

      {/* Portal dropdown — renders at body level, escapes overflow:hidden */}
      {isOpen && ReactDOM.createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            top: menuPos.top,
            left: menuPos.left,
            width: menuPos.width,
            maxHeight: '240px',
            overflowY: 'auto',
            backgroundColor: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            padding: '4px',
            zIndex: 9999
          }}
        >
          {colorOptions.map((option, index) => {
            const isSelected = value?.toLowerCase() === option.value.toLowerCase();
            return (
              <button
                key={`${option.value}-${index}`}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  width: '100%',
                  padding: '7px 10px',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: '#111827',
                  backgroundColor: isSelected ? '#f0f0ff' : 'transparent',
                  transition: 'background-color 0.1s'
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <span style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '5px',
                  backgroundColor: option.value,
                  border: '1px solid rgba(0,0,0,0.1)',
                  flexShrink: 0
                }} />
                <span style={{ flex: 1, textAlign: 'left' }}>{option.name}</span>
                {isSelected && (
                  <span style={{ fontSize: '14px', color: '#6366f1' }}>&#10003;</span>
                )}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
};

export default ColorPickerField;
